import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, Group, Modal} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {Owner} from 'auth/PermissionConstants';
import {EntitySummary} from 'constants/UserConstants';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {TemplateCollection} from 'notebook/entry-templates/TemplateConstants';
import TemplateDAO from 'notebook/entry-templates/TemplateDAO';
import notifications from 'notifications';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import ConfigureExecutionTemplateModalBody, {
  ExecutionTemplateType,
} from 'processes/settings/ConfigureExecutionTemplateModalBody';
import {NameField, OwnerField, PrefixField} from 'processes/settings/ProcessSchemaMetadataSectionRow';
import {cacheUpdatedTaskSchema, isEmptyOrWhitespace} from 'processes/settings/ProcessTaskSchemaUtils';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {Header1} from 'ui-library/Headers';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import ModalFooter from 'ui-library/modals/ModalFooter';
import {assertNever} from 'util/Types';

import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';

interface CloneProcessTaskSchemaModalProps {
  originalProcessTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  actionOrigin: string;
  actionMethod: string;
  onCloneSuccess: (processTaskSchema: ProcessTaskSchemaGraphQL) => void;
  onRequestClose: () => void;
}

export default function CloneProcessTaskSchemaModal(props: CloneProcessTaskSchemaModalProps): JSX.Element {
  const [name, setName] = useState<string>(`${props.originalProcessTaskSchema.name} (Duplicate)`);
  const [prefix, setPrefix] = useState<string>('');
  const [owner, setOwner] = useState<EntitySummary | null>(props.originalProcessTaskSchema.organization);
  const schemaOwnerChoices = getSchemaOwnerChoices();

  const isEntryExecutionType = props.originalProcessTaskSchema.executionType === ProcessExecutionType.ENTRY;

  const [executionTemplateType, setExecutionTemplateType] = useState<ExecutionTemplateType>(
    ExecutionTemplateType.CLONE_EXISTING_TEMPLATE
  );
  const [templateCollections, setTemplateCollections] = useState<Array<TemplateCollection> | null>(null);
  const [templateCollection, setTemplateCollection] = useState<TemplateCollection | null>(null);
  const [templateName, setTemplateName] = useState<string | null>(
    props.originalProcessTaskSchema.entryTemplate?.name ?? null
  );

  const [sourceTemplateId, setSourceTemplateId] = useState<string | null>(
    props.originalProcessTaskSchema.entryTemplate?.id || null
  );

  const [isSubmitEnabled, setIsSubmitEnabled] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useStateIfMounted<boolean>(false);

  const [errors, setErrors] = useState<{[errorKey: string]: string}>({});

  useEffect(() => {
    if (isEntryExecutionType) {
      TemplateDAO.fetchCollections('write').done((collections: Array<TemplateCollection>) => {
        setTemplateCollections(collections);
        const initialTemplateCollectionId =
          props.originalProcessTaskSchema.entryTemplate?.templateCollection.id;
        if (initialTemplateCollectionId != null) {
          const initialTemplateCollection = collections.find(
            (collection) => collection.id === initialTemplateCollectionId
          );
          setTemplateCollection(initialTemplateCollection ?? null);
        }
      });
    }
  }, [isEntryExecutionType, props.originalProcessTaskSchema]);

  useEffect(() => {
    const areTaskSchemaDetailsFilled =
      !(isEmptyOrWhitespace(name) || isEmptyOrWhitespace(prefix)) && Boolean(owner);
    if (!isEntryExecutionType) {
      setIsSubmitEnabled(areTaskSchemaDetailsFilled);
    } else {
      const areNameAndCollectionSet =
        templateName != null && !isEmptyOrWhitespace(templateName) && Boolean(templateCollection);
      switch (executionTemplateType) {
        case ExecutionTemplateType.CREATE_BLANK_TEMPLATE:
          setIsSubmitEnabled(areTaskSchemaDetailsFilled && areNameAndCollectionSet);
          return;
        case ExecutionTemplateType.CLONE_EXISTING_TEMPLATE:
          setIsSubmitEnabled(
            areTaskSchemaDetailsFilled && areNameAndCollectionSet && Boolean(sourceTemplateId)
          );
          return;
        default:
          assert(false, `Unexpected execution type: ${assertNever(executionTemplateType)}`);
      }
    }
  }, [
    isEntryExecutionType,
    name,
    owner,
    prefix,
    templateCollection,
    templateName,
    sourceTemplateId,
    executionTemplateType,
    props.originalProcessTaskSchema,
  ]);

  const cloneProcessTaskSchema = async (): Promise<void> => {
    setErrors({});

    const baseRequestData = {
      processTaskSchemaId: props.originalProcessTaskSchema.id,
      taskGroupPrefix: prefix,
      name,
      organizationId: owner!.id,
      actionOrigin: props.actionOrigin,
      actionMethod: props.actionMethod,
    };

    let requestData: ProcessSchemaDAO.CloneProcessTaskSchemaRequest = baseRequestData;
    if (isEntryExecutionType) {
      requestData = {
        ...baseRequestData,
        executionTemplate: {
          templateCollectionId: templateCollection!.id,
          templateName: templateName!,
          sourceTemplateId:
            executionTemplateType === ExecutionTemplateType.CLONE_EXISTING_TEMPLATE ? sourceTemplateId : null,
          publishOnSchemaCreation: false,
        },
      };
    }

    setIsSubmitting(true);
    try {
      const processTaskSchema = await ProcessSchemaDAO.cloneProcessTaskSchema(requestData);
      cacheUpdatedTaskSchema(processTaskSchema);
      props.onRequestClose();
      props.onCloneSuccess(processTaskSchema);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
      if (e.responseJSON) {
        setErrors(e.responseJSON.errors ?? {});
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={true}
      onClose={props.onRequestClose}
      title="Duplicate schema"
      withCloseButton={!isSubmitting}
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      closeButtonProps={{'aria-label': 'Close'}}
      overlayProps={{
        className: '',
        ...getTestProps({component: 'CloneProcessTaskSchemaModal', element: 'overlay'}),
      }}
    >
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (isSubmitEnabled && !isSubmitting) {
            cloneProcessTaskSchema();
          }
        }}
      >
        <div>
          <div className={css(styles.section)}>
            <div className={css(styles.row)}>
              <Header1>New task schema details</Header1>
            </div>
            <Row className={css(styles.row)}>
              <Col sm={6}>
                <NameField
                  label="Name"
                  value={name}
                  error={errors.processTaskSchemaName ?? ''}
                  isBeingCreated={true}
                  isDisabled={false}
                  onChange={(updatedName: string) => {
                    if (updatedName !== name) {
                      setName(updatedName);
                    }
                  }}
                  isRequired={false}
                  dataTestAttrs={{component: 'CloneProcessTaskSchemaModal', element: 'nameField'}}
                />
              </Col>
              <Col sm={6}>
                <PrefixField
                  label="Task group prefix"
                  value={prefix}
                  error={errors.taskGroupPrefix ?? ''}
                  isBeingCreated={true}
                  isDisabled={false}
                  onChange={(updatedPrefix: string) => {
                    if (updatedPrefix !== prefix) {
                      setPrefix(updatedPrefix);
                    }
                  }}
                  isRequired={false}
                  dataTestAttrs={{component: 'CloneProcessTaskSchemaModal', element: 'prefixField'}}
                />
              </Col>
            </Row>
            <Row className={css(styles.row)}>
              <Col sm={6}>
                <OwnerField
                  isEditable={true}
                  ownerChoices={schemaOwnerChoices}
                  owner={owner}
                  onOwnerChanged={(updatedOwner: Owner) => {
                    if (updatedOwner !== owner) {
                      setOwner(updatedOwner);
                    }
                  }}
                  isRequired={false}
                  dataTestAttrs={{component: 'CloneProcessTaskSchemaModal', element: 'ownerField'}}
                />
              </Col>
            </Row>
          </div>
          <div className={css(styles.section)}>
            {isEntryExecutionType ? (
              <div>
                <div className={css(styles.row)}>
                  <Header1>Configure execution template</Header1>
                </div>
                <ConfigureExecutionTemplateModalBody
                  templateCollections={templateCollections}
                  templateName={templateName!}
                  setTemplateName={setTemplateName}
                  selectedTemplateCollection={templateCollection}
                  setSelectedTemplateCollection={setTemplateCollection}
                  sourceTemplateId={sourceTemplateId}
                  setSourceTemplateId={setSourceTemplateId}
                  executionTemplateType={executionTemplateType}
                  setExecutionTemplateType={setExecutionTemplateType}
                  toggleGroupOptions={[
                    {
                      id: ExecutionTemplateType.CLONE_EXISTING_TEMPLATE,
                      buttonProps: {
                        text: 'Duplicate existing template',
                      },
                    },
                    {
                      id: ExecutionTemplateType.CREATE_BLANK_TEMPLATE,
                      buttonProps: {
                        text: 'Create blank template',
                      },
                    },
                  ]}
                  sourceTemplateError={errors.sourceTemplate ?? ''}
                />
              </div>
            ) : null}
          </div>
        </div>
        <ModalFooter>
          <Group position="right">
            <Button
              type="submit"
              disabled={!isSubmitEnabled || isSubmitting}
              loading={isSubmitting}
              {...getTestProps({
                component: 'CloneProcessTaskSchemaModal',
                element: 'submitButton',
              })}
            >
              Duplicate schema
            </Button>
          </Group>
        </ModalFooter>
      </Box>
    </Modal>
  );
}

const styles = StyleSheet.create({
  row: {
    marginBottom: 12,
  },
  section: {
    marginBottom: 24,
  },
});
